import axios from 'axios';
import { API_BASE } from '../config';
import { getInventorySettingsStorageKey, getInventoryCategoriesStorageKey } from './inventoryStorage';
import { defaultVisibleColumns, defaultRequiredFields } from './inventoryFieldDefs';

// E.2: konfiguracja pól magazynu przeniesiona z localStorage na serwer (org-scoped).
// Ten moduł jest czysty poza dwiema cienkimi funkcjami fetch/save — reszta (mapowanie,
// migracja, cache) jest testowalna bez axios/window.

export async function fetchInventorySettings() {
  const res = await axios.get(`${API_BASE}/api/inventory-settings`, { withCredentials: true });
  return res.data;
}

export async function saveInventorySettings(payload) {
  const csrf = (typeof window !== 'undefined' && window.localStorage.getItem('csrfToken')) || '';
  const res = await axios.put(`${API_BASE}/api/inventory-settings`, payload, {
    withCredentials: true,
    headers: { 'X-CSRF-Token': csrf },
  });
  return res.data;
}

// Serwer -> kształt, którego oczekują dzisiejsze loadSettings()/loadInventorySettings()
// w InventorySettings.js / AddEquipment.js / EquipmentDetails.js / pages/inventory/*.
export function fromServerPayload(data) {
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const sorted = [...fields].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  return {
    visibleColumns: sorted.filter(f => f.visible).map(f => f.key),
    requiredFields: sorted.filter(f => f.required).map(f => f.key),
    customColumns: sorted.filter(f => f.isCustom).map(f => ({
      key: f.key,
      labelKey: null,
      label: f.label || f.key,
      description: f.example || f.label || f.key,
      group: 'custom',
      example: f.example || '',
      fieldType: f.fieldType || 'text',
      options: f.options || undefined,
    })),
    categoryRequirements: data?.categoryRequirements || {},
    categories: Array.isArray(data?.categories) ? data.categories : [],
  };
}

// Lokalny stan (localStorage shape) -> payload PUT. `key` kolumn własnych jest ZAWSZE
// wzięty dosłownie z customColumns — nigdy przeliczany z label (pułapka z E.2).
export function toServerPayload(local = {}) {
  // E.2 (poprawka po E2E): BRAK klucza to nie to samo co PUSTA lista. Konwencja localStorage
  // od zawsze brzmiała „brak/pusto = domyślne" (tak czytają go `InventorySettings.js` i
  // `pages/inventory/*`), a `visibleColumns = []` w destrukturyzacji zamieniało brak klucza
  // na „żadnych widocznych kolumn". Trafiało to w realny przepływ: `AddEquipment.js` zapisuje
  // do localStorage obiekt BEZ `visibleColumns`, a jednorazowa migracja z E.2 brała ten
  // niepełny obiekt i wysyłała na serwer wszystkie pola z `visible:false` — po czym tabela
  // magazynu renderowała wiersze bez ani jednej kolumny. Jawnie pusta tablica (użytkownik
  // odznaczył wszystko) jest uszanowana, brak klucza — nie.
  const visibleColumns = Array.isArray(local.visibleColumns) ? local.visibleColumns : defaultVisibleColumns;
  const requiredFields = Array.isArray(local.requiredFields) ? local.requiredFields : defaultRequiredFields;
  const categoryRequirements = local.categoryRequirements || {};
  const categories = Array.isArray(local.categories) ? local.categories : [];
  const customColumns = Array.isArray(local.customColumns) ? local.customColumns : [];

  const customByKey = new Map(customColumns.map(c => [c.key, c]));
  const orderedKeys = Array.from(new Set([
    ...visibleColumns,
    ...requiredFields,
    ...customColumns.map(c => c.key),
  ]));

  const fields = orderedKeys.map((key, position) => {
    const custom = customByKey.get(key);
    if (custom) {
      return {
        key,
        label: custom.label ?? null,
        example: custom.example || null,
        required: requiredFields.includes(key),
        visible: visibleColumns.includes(key),
        position,
        fieldType: custom.fieldType || 'text',
        options: custom.fieldType === 'list' ? (custom.options || []) : null,
        isCustom: true,
      };
    }
    return {
      key,
      label: null,
      example: null,
      required: requiredFields.includes(key),
      visible: visibleColumns.includes(key),
      position,
      fieldType: 'text',
      options: null,
      isCustom: false,
    };
  });

  return { fields, categoryRequirements, categories };
}

// `localSettings` = obiekt sparsowany z surowego localStorage.getItem(...), albo null
// gdy klucza nie było. Migrujemy TYLKO gdy serwer jeszcze nic nie ma, a przeglądarka
// faktycznie niesie KONFIGURACJĘ — nigdy nie nadpisujemy już skonfigurowanej org.
//
// Sama obecność kluczy nie wystarcza (2026-09-17): `App.js#syncCategories` przy każdym starcie
// wpisuje do localStorage skorupę `{ categoryRequirements: {}, categories: [] }`, więc stary
// warunek „jakiekolwiek klucze" migrował NICZYM nieustawioną konfigurację — `toServerPayload`
// uzupełniał braki domyślnymi kolumnami i serwer od tej chwili twierdził, że firma jest
// skonfigurowana. Zależnie od tempa (na CI tak, lokalnie zwykle nie) zmieniało to zachowanie
// Ustawień magazynu i czerwieniło inventory_presets.cy.js.
//
// To samo z NIEPUSTĄ skorupą (12.10.2026): `syncCategories` wpisuje do niej nazwy kategorii
// z bazy firmy, każdą z pustą listą pól (`{ Narty: [] }`), więc firma ze słownikiem typów
// po samym zalogowaniu i odświeżeniu strony była migrowana na serwer jako „skonfigurowana”
// (krok „Pola magazynu” odhaczony, inventory_settings_server.cy.js czerwony na configured:true).
// Same nazwy kategorii to dane z bazy, nie konfiguracja; konfiguracją są kolumny, pola wymagane,
// kolumny własne albo pola przypisane do kategorii (niepusta lista).
export function shouldMigrateLocal({ configured, localSettings }) {
  if (configured) return false;
  if (!localSettings || typeof localSettings !== 'object') return false;
  const l = localSettings;
  const nonEmptyArray = (v) => Array.isArray(v) && v.length > 0;
  return Array.isArray(l.visibleColumns)
    || Array.isArray(l.requiredFields)
    || nonEmptyArray(l.customColumns)
    || (!!l.categoryRequirements && typeof l.categoryRequirements === 'object'
      && Object.values(l.categoryRequirements).some(nonEmptyArray));
}

export function writeLocalCache(settingsShape) {
  if (typeof window === 'undefined') return;
  try {
    const settingsKey = getInventorySettingsStorageKey();
    const categoriesKey = getInventoryCategoriesStorageKey();
    window.localStorage.setItem(settingsKey, JSON.stringify(settingsShape));
    window.localStorage.setItem(categoriesKey, JSON.stringify(settingsShape.categories || []));
  } catch (_) { /* localStorage niedostępny (tryb prywatny itp.) — cache jest tylko optymalizacją */ }
}
